import { useState, useEffect } from 'react';
import {
  TextLink,
  FAIcon,
  IconStyle,
  useBreakpoint,
  Breakpoints,
  useNextgenRouter,
  TextLinkUnderlineStyle,
} from '@nextgen-web-framework/all';
import { nextgenThrottle } from '@nextgen-web-framework/core';
import { CMSDataType } from '../types/common';
import styles from './MenuDropdown.module.scss';

interface MenuDropdownProps {
  menuData: CMSDataType[];
  activeRoute: string;
  content: React.JSX.Element;
}
const MenuDropdown = ({ menuData, activeRoute, content }: MenuDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  let activeMenu = menuData.find((menu: CMSDataType) => menu.url === activeRoute);
  if (!activeMenu) {
    activeMenu = [menuData[0]];
  }
  const viewPortType = useBreakpoint();
  const router = useNextgenRouter();
  const isDesktop = viewPortType === Breakpoints.LG || viewPortType === Breakpoints.XL;
  useEffect(() => {
    const throttledScrollHandler = nextgenThrottle(() => {
      const target = document.getElementById('menu-dropdown-id');
      const rect = target?.getBoundingClientRect();
      if (rect && rect?.top <= 78) {
        target?.classList.add(`${styles['menu-dropdown-scrolled']}`);
      } else {
        target?.classList.remove(`${styles['menu-dropdown-scrolled']}`);
      }
    }, 500);
    window.addEventListener('scroll', throttledScrollHandler as EventListener);
    return () => {
      window.removeEventListener('scroll', throttledScrollHandler as EventListener);
    };
  }, []);
  if (isDesktop) {
    return null;
  }
  return (
    <div className={styles['menu-dropdown-wrapper']}>
      <div
        className={`${styles['menu-dropdown-container']} ${styles['menu-dropdown-sticky']}`}
        id="menu-dropdown-id"
        data-testid="menu-dropdown-container-test-id"
      >
        <div
          className={styles['menu-dropdown-menu']}
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={() => setIsOpen(!isOpen)}
          data-testid="menu-dropdown-menu-test-id"
          role="button"
          aria-label="selected dropdown item"
          tabIndex={0}
        >
          <span className={styles['menu-dropdown-title']}>
            <TextLink text={activeMenu.label} href={activeMenu.url} underline={TextLinkUnderlineStyle.NOHOVER} />
          </span>
          <span className={styles['menu-dropdown-icon']}>
            {isOpen ? (
              <FAIcon iconName={'chevron-up'} iconStyle={IconStyle.SOLID} />
            ) : (
              <FAIcon iconName={'chevron-down'} iconStyle={IconStyle.SOLID} />
            )}
          </span>
        </div>
        {isOpen && (
          <div className={styles['menu-dropdown-list']} data-testid="menu-dropdown-list-test-id">
            {menuData.map((menu: CMSDataType) => (
              <>
                <div
                  className={`${styles['menu-dropdown-list-item']} ${
                    activeMenu.url === menu.url ? styles['menu-dropdown-active'] : ''
                  }`}
                  role="button"
                  aria-label="content dropdown item"
                  tabIndex={0}
                  key={menu.url}
                  onClick={() => (menu.target === '_blank' ? window.open(menu.url, '_blank') : router.push(menu.url))}
                  onKeyDown={() => (menu.target === '_blank' ? window.open(menu.url, '_blank') : router.push(menu.url))}
                >
                  {menu.label}
                </div>
              </>
            ))}
          </div>
        )}
      </div>
      <div className={styles['menu-dropdown-content']}>{content}</div>
    </div>
  );
};

export default MenuDropdown;
